/**
 * Path Network Utilities
 *
 * Functions for building a graph structure from path features (LineString
 * and `path_wide` Polygon plazas) for routing and wayfinding calculations.
 */

import type { Feature, LineString, Polygon, MultiPolygon, Position } from "geojson";
import {
  booleanPointInPolygon,
  lineIntersect,
  lineString as turfLineString,
  nearestPointOnLine,
  point as turfPoint,
  polygon as turfPolygon,
} from "@turf/turf";
import { calcDistance, coordToId } from "./spatial";

/** Layer types that participate in wayfinding */
export const PATH_LAYER_TYPES: string[] = ["path_primary", "path_secondary", "path_tertiary", "path_wide", "bridge", "tunnel"];

/** Polygon plaza fallback caps. See synthesizePolygonInteriorEdges. */
const PLAZA_PERIMETER_CAP = 50;
const PLAZA_TOTAL_CANDIDATE_CAP = 30;

/** GeoJSON `Position` is `number[]` (variable length). Path code only ever uses lng/lat. */
function toLngLat(p: Position): [number, number] {
  return [p[0], p[1]];
}

/** A node in the path network (endpoint or intersection) */
export interface PathNode {
  id: string;
  coords: [number, number];
  connections: string[];
}

/** Accessibility metadata for a path edge */
export interface PathEdgeMetadata {
  hasStairs?: boolean;
  steepGrade?: boolean;
  wayfindingWeight?: number;
}

/**
 * Classification of how an edge was contributed to the graph.
 *
 *  - "line"             — consecutive coord pair of a LineString feature.
 *  - "plaza-perimeter"  — adjacent vertex pair on a polygon's outer ring.
 *  - "plaza-interior"   — visibility-graph chord across a plaza interior
 *                         (between non-adjacent perimeter vertices, or
 *                          between a perimeter vertex and an interior
 *                          sibling endpoint, or between two interior
 *                          siblings). Abstract by design — these don't
 *                          correspond to a drawn line.
 *
 * Plan 121 introduces this so edge-snap dispatch can route differently
 * when a click lands on a real corridor (line / perimeter) vs. an abstract
 * plaza chord.
 */
export type PathEdgeKind = "line" | "plaza-perimeter" | "plaza-interior";

/** An edge connecting two nodes */
export interface PathEdge {
  from: string;
  to: string;
  distance: number;
  kind: PathEdgeKind;
  /** Source layer's `layerType` (e.g. "path_primary"). Used by the long-edge
   *  warning rule + future per-layer-type routing rules. Undefined only if
   *  the feature had no layerType property. */
  layerType?: string;
  /** Plaza key for plaza-perimeter and plaza-interior edges. For a single
   *  Polygon feature this is the feature's layerId; for a MultiPolygon ring
   *  set this is `${layerId}#${ringSetIndex}` so multiple plazas on one
   *  layer remain distinct. Undefined on "line" edges. */
  plazaId?: string;
  metadata?: PathEdgeMetadata;
}

/**
 * Per-plaza metadata indexed by `plazaId`. Built once during
 * `buildPathNetwork` and consumed by the snap dispatch in `findRoute` (Plan
 * 121 commit 3) so visibility-graph queries don't have to rebuild
 * ringLineStrings / candidates on every route call.
 */
export interface PlazaInfo {
  plazaId: string;
  /** Source feature's layerId. Matches `polygonLayerId` on the contributed
   *  edges' metadata, useful for diagnostic logging. */
  layerId: string;
  /** Ring sets (outer + holes), exactly as passed to buildPathNetwork. */
  rings: Position[][];
  /** Pre-built `turf.lineString` for each ring — reused by
   *  `segmentCrossesAnyRing` so we don't pay the turf-feature build cost on
   *  every snap. */
  ringLineStrings: Feature<LineString>[];
  /** Perimeter vertices + interior sibling endpoints, in the same order
   *  `synthesizePolygonInteriorEdges` uses internally. */
  candidates: Position[];
}

/** The complete path network graph */
export interface PathNetwork {
  nodes: Map<string, PathNode>;
  edges: Map<string, PathEdge>;
  /** Plaza metadata for snap dispatch (Plan 121). Empty when no polygon
   *  features were present in `pathFeatures`. */
  plazas: Map<string, PlazaInfo>;
}


/**
 * Generate a unique edge ID from two node IDs. Exported so the overlay
 * accessors (Plan 121 commit 2) can compute matching keys when looking edges
 * up across the original network + virtual overlay.
 */
export function edgeId(fromId: string, toId: string): string {
  // Sort to ensure consistent ID regardless of direction
  return fromId < toId ? `${fromId}|${toId}` : `${toId}|${fromId}`;
}


/**
 * Build a path network graph from LineString and `path_wide` Polygon features.
 *
 * - LineString / MultiLineString features contribute one edge per consecutive
 *   coordinate pair (existing behaviour).
 * - Polygon / MultiPolygon features (`path_wide` plazas) contribute perimeter
 *   edges plus a visibility graph between perimeter vertices and any sibling
 *   path endpoint that falls in/on the polygon — letting the router cut
 *   diagonally across plazas instead of routing around the perimeter. See
 *   `synthesizePolygonInteriorEdges` for caps + fallback rules.
 *
 * Nodes at the same location (within tolerance) are merged via `coordToId`.
 *
 * @param pathFeatures - Array of LineString or Polygon path features
 * @returns PathNetwork with nodes and edges
 */
export function buildPathNetwork(
  pathFeatures: Feature<LineString | Polygon | MultiPolygon>[]
): PathNetwork {
  const nodes = new Map<string, PathNode>();
  const edges = new Map<string, PathEdge>();
  const plazas = new Map<string, PlazaInfo>();

  // Pre-collect line endpoints for plaza visibility-graph candidates.
  const lineEndpoints: { coord: Position; layerId: string | undefined }[] = [];
  for (const feature of pathFeatures) {
    if (feature.geometry.type !== "LineString") continue;
    const coords = feature.geometry.coordinates;
    if (coords.length < 2) continue;
    lineEndpoints.push({ coord: coords[0], layerId: feature.properties?.layerId });
    lineEndpoints.push({ coord: coords[coords.length - 1], layerId: feature.properties?.layerId });
  }

  // Process each path feature
  for (const feature of pathFeatures) {
    const geom = feature.geometry;
    const layerId = (feature.properties?.layerId as string | undefined) ?? "<unknown>";

    if (geom.type === "LineString") {
      addLineSegmentsToNetwork(geom.coordinates as [number, number][], feature.properties, nodes, edges);
      continue;
    }

    if (geom.type === "Polygon") {
      // Single Polygon: plazaId is the layerId itself.
      const plaza = addPolygonRingsToNetwork(
        geom.coordinates as Position[][], feature, layerId, lineEndpoints, nodes, edges
      );
      if (plaza) plazas.set(plaza.plazaId, plaza);
      continue;
    }

    if (geom.type === "MultiPolygon") {
      // MultiPolygon: each ring set is an independent plaza, suffixed by index
      // so two plazas on the same layer remain distinct in `plazas`.
      const ringSets = geom.coordinates as Position[][][];
      for (let i = 0; i < ringSets.length; i++) {
        const plazaId = ringSets.length === 1 ? layerId : `${layerId}#${i}`;
        const plaza = addPolygonRingsToNetwork(
          ringSets[i], feature, plazaId, lineEndpoints, nodes, edges
        );
        if (plaza) plazas.set(plaza.plazaId, plaza);
      }
      continue;
    }
  }

  return { nodes, edges, plazas };
}

/** Add edges between consecutive coordinates of a LineString. */
function addLineSegmentsToNetwork(
  coords: [number, number][],
  properties: Feature["properties"],
  nodes: Map<string, PathNode>,
  edges: Map<string, PathEdge>
): void {
  if (coords.length < 2) return;

  for (let i = 0; i < coords.length - 1; i++) {
    addEdge(coords[i], coords[i + 1], properties, "line", undefined, nodes, edges);
  }
}

/** Add an edge between two coordinates (and ensure both nodes exist). */
function addEdge(
  from: Position,
  to: Position,
  properties: Feature["properties"],
  kind: PathEdgeKind,
  plazaId: string | undefined,
  nodes: Map<string, PathNode>,
  edges: Map<string, PathEdge>
): void {
  const fromLL = toLngLat(from);
  const toLL = toLngLat(to);
  const startId = coordToId(fromLL);
  const endId = coordToId(toLL);
  if (startId === endId) return;

  if (!nodes.has(startId)) {
    nodes.set(startId, { id: startId, coords: fromLL, connections: [] });
  }
  if (!nodes.has(endId)) {
    nodes.set(endId, { id: endId, coords: toLL, connections: [] });
  }

  const startNode = nodes.get(startId)!;
  const endNode = nodes.get(endId)!;
  if (!startNode.connections.includes(endId)) startNode.connections.push(endId);
  if (!endNode.connections.includes(startId)) endNode.connections.push(startId);

  const eid = edgeId(startId, endId);
  if (!edges.has(eid)) {
    edges.set(eid, {
      from: startId,
      to: endId,
      distance: calcDistance(fromLL, toLL),
      kind,
      layerType: typeof properties?.layerType === "string" ? properties.layerType : undefined,
      plazaId,
      metadata: {
        hasStairs: properties?.hasStairs === true,
        steepGrade: properties?.steepGrade === true,
        wayfindingWeight: typeof properties?.wayfindingWeight === "number" ? properties.wayfindingWeight : undefined,
      },
    });
  }
}

/**
 * Process a single Polygon (one ring set) into perimeter edges + visibility-graph
 * interior edges. Holes (rings beyond the first) act as obstacles for visibility.
 *
 * Returns the PlazaInfo for indexing in `network.plazas`, or null if the ring
 * set was invalid (too few coords).
 */
function addPolygonRingsToNetwork(
  rings: Position[][],
  feature: Feature<LineString | Polygon | MultiPolygon>,
  plazaId: string,
  lineEndpoints: { coord: Position; layerId: string | undefined }[],
  nodes: Map<string, PathNode>,
  edges: Map<string, PathEdge>
): PlazaInfo | null {
  if (rings.length === 0) return null;
  const outerRing = rings[0];
  if (!outerRing || outerRing.length < 4) return null; // Polygon ring needs at least 4 coords (closed triangle).

  // Perimeter vertices: drop the duplicated closing vertex (last === first in valid polygons).
  const perimeter = outerRing.slice(0, -1);

  // Always emit perimeter edges so a Polygon plaza is at minimum walkable around its border.
  for (let i = 0; i < perimeter.length; i++) {
    const a = perimeter[i];
    const b = perimeter[(i + 1) % perimeter.length];
    addEdge(a, b, feature.properties, "plaza-perimeter", plazaId, nodes, edges);
  }

  // Synthesize visibility-graph interior edges (with caps + fallback). Returns
  // the candidate list so we can populate PlazaInfo without recomputing.
  const candidates = synthesizePolygonInteriorEdges(
    rings, perimeter, feature, plazaId, lineEndpoints, nodes, edges
  );

  const layerId = (feature.properties?.layerId as string | undefined) ?? "<unknown>";
  return {
    plazaId,
    layerId,
    rings,
    ringLineStrings: rings.map((r) => turfLineString(r)),
    candidates,
  };
}

/**
 * Build the inside-plaza visibility graph.
 *
 * Candidates:
 *   - All perimeter (outer-ring) vertices.
 *   - Sibling-line endpoints (from any path-layer-type LineString) that fall in/on
 *     the polygon. ENDPOINTS ONLY — never arbitrary internal vertices, which would
 *     create phantom shortcuts through paths that merely pass over the plaza.
 *   - Junction vertices already present (collapsed by `coordToId`) where multiple
 *     paths meet inside the plaza — included implicitly via `nodes.has(...)` check.
 *
 * Caps (either trips fallback):
 *   - Outer-ring vertex count > PLAZA_PERIMETER_CAP (50).
 *   - Total candidate count (perimeter + interior siblings) > PLAZA_TOTAL_CANDIDATE_CAP (30).
 *
 * Fallback: perimeter-only graph + one direct edge from each interior sibling
 * endpoint to its nearest perimeter vertex. Tiebreaker: lower perimeter index wins.
 *
 * Normal path: for each unordered (u, v) candidate pair build segment [u,v]; reject
 * if it crosses the outer ring or any hole strictly between u and v.
 */
function synthesizePolygonInteriorEdges(
  rings: Position[][],
  perimeter: Position[],
  feature: Feature<LineString | Polygon | MultiPolygon>,
  plazaId: string,
  lineEndpoints: { coord: Position; layerId: string | undefined }[],
  nodes: Map<string, PathNode>,
  edges: Map<string, PathEdge>
): Position[] {
  const layerId = (feature.properties?.layerId as string | undefined) ?? "<unknown>";
  const polygonForBPIP = turfPolygon(rings);

  // Collect interior sibling endpoints (in/on the polygon, deduped by coordToId,
  // excluding endpoints whose coordToId is already a perimeter vertex).
  const perimeterIds = new Set(perimeter.map((p) => coordToId(toLngLat(p))));
  const interiorSiblings: Position[] = [];
  const seenInteriorIds = new Set<string>();
  for (const ep of lineEndpoints) {
    const id = coordToId(toLngLat(ep.coord));
    if (perimeterIds.has(id) || seenInteriorIds.has(id)) continue;
    if (booleanPointInPolygon(ep.coord, polygonForBPIP, { ignoreBoundary: false })) {
      interiorSiblings.push(ep.coord);
      seenInteriorIds.add(id);
    }
  }

  const totalCandidates = perimeter.length + interiorSiblings.length;
  const fallback =
    perimeter.length > PLAZA_PERIMETER_CAP || totalCandidates > PLAZA_TOTAL_CANDIDATE_CAP;

  if (fallback) {
    console.warn(
      "[pathNetwork] plaza %s using fallback graph (perimeter=%d, total=%d)",
      layerId,
      perimeter.length,
      totalCandidates
    );
    // Connect each interior sibling to its single nearest perimeter vertex.
    // Tiebreaker: lower perimeter index wins (deterministic, makes tests stable).
    for (const sibling of interiorSiblings) {
      let nearestIdx = -1;
      let nearestDist = Infinity;
      const siblingLL = toLngLat(sibling);
      for (let i = 0; i < perimeter.length; i++) {
        const d = calcDistance(siblingLL, toLngLat(perimeter[i]));
        if (d < nearestDist) {
          nearestDist = d;
          nearestIdx = i;
        }
      }
      if (nearestIdx >= 0) {
        addEdge(sibling, perimeter[nearestIdx], feature.properties, "plaza-interior", plazaId, nodes, edges);
      }
    }
    return [...perimeter, ...interiorSiblings];
  }

  // Normal path: full visibility graph.
  // Pre-build ring LineStrings once per polygon for lineIntersect.
  const ringLineStrings = rings.map((ring) => turfLineString(ring));
  const candidates: Position[] = [...perimeter, ...interiorSiblings];

  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const u = candidates[i];
      const v = candidates[j];
      // Skip adjacent perimeter pairs — already emitted as perimeter edges above.
      if (i < perimeter.length && j < perimeter.length) {
        const adj = j - i === 1 || (i === 0 && j === perimeter.length - 1);
        if (adj) continue;
      }

      const segment = turfLineString([u, v]);
      if (segmentCrossesAnyRing(segment, u, v, ringLineStrings)) continue;

      addEdge(u, v, feature.properties, "plaza-interior", plazaId, nodes, edges);
    }
  }

  return candidates;
}

/**
 * Reject a segment if any ring intersects it strictly between the two endpoints.
 * Allows shared endpoints (the segment ends ON the ring) — that is by construction
 * for perimeter-vertex pairs.
 *
 * Exported so the snap dispatch (Plan 121 commit 3) can reuse the same
 * visibility check that graph construction uses. Two consumers, one helper —
 * keeps the "what does it mean to be visible across a plaza" answer
 * single-sourced.
 */
export function segmentCrossesAnyRing(
  segment: Feature<LineString>,
  u: Position,
  v: Position,
  ringLineStrings: Feature<LineString>[]
): boolean {
  const uId = coordToId(toLngLat(u));
  const vId = coordToId(toLngLat(v));
  for (const ring of ringLineStrings) {
    const hits = lineIntersect(segment, ring);
    for (const hit of hits.features) {
      const hitId = coordToId(toLngLat(hit.geometry.coordinates as Position));
      if (hitId === uId || hitId === vId) continue; // shared endpoint, OK
      return true;
    }
  }
  return false;
}

/**
 * Extract every edge contributed by a plaza Polygon (perimeter + visibility
 * graph + fallback edges) as a flat FeatureCollection of LineStrings. Designed
 * for admin debug overlays so admins can see the wayfinding edges the router
 * actually has access to inside a plaza, not just the polygon's outline.
 *
 * Mirrors the logic in `synthesizePolygonInteriorEdges` so the overlay matches
 * the live network exactly. Each output feature carries `kind = "perimeter"`
 * or `"interior"` for separate styling.
 */
export interface PlazaEdgeProperties {
  layerId?: string;
  kind: "perimeter" | "interior";
}

export function extractPlazaEdges(
  pathFeatures: Feature<LineString | Polygon | MultiPolygon>[]
): Feature<LineString, PlazaEdgeProperties>[] {
  const out: Feature<LineString, PlazaEdgeProperties>[] = [];

  // Same line-endpoint pre-pass as buildPathNetwork — needed for visibility-graph
  // candidate selection.
  const lineEndpoints: Position[] = [];
  for (const f of pathFeatures) {
    if (f.geometry.type !== "LineString") continue;
    const coords = f.geometry.coordinates;
    if (coords.length < 2) continue;
    lineEndpoints.push(coords[0], coords[coords.length - 1]);
  }

  for (const f of pathFeatures) {
    const layerId = f.properties?.layerId as string | undefined;
    const ringSets: Position[][][] = [];
    if (f.geometry.type === "Polygon") ringSets.push(f.geometry.coordinates as Position[][]);
    else if (f.geometry.type === "MultiPolygon") {
      for (const rings of f.geometry.coordinates as Position[][][]) ringSets.push(rings);
    }
    for (const rings of ringSets) {
      pushPlazaEdgesForRings(rings, layerId, lineEndpoints, out);
    }
  }

  return out;
}

function pushPlazaEdgesForRings(
  rings: Position[][],
  layerId: string | undefined,
  lineEndpoints: Position[],
  out: Feature<LineString, PlazaEdgeProperties>[]
): void {
  if (rings.length === 0) return;
  const outerRing = rings[0];
  if (!outerRing || outerRing.length < 4) return;
  const perimeter = outerRing.slice(0, -1);

  const pushEdge = (a: Position, b: Position, kind: "perimeter" | "interior") => {
    out.push({
      type: "Feature",
      properties: { layerId, kind },
      geometry: { type: "LineString", coordinates: [a, b] },
    });
  };

  // Perimeter edges.
  for (let i = 0; i < perimeter.length; i++) {
    pushEdge(perimeter[i], perimeter[(i + 1) % perimeter.length], "perimeter");
  }

  // Interior visibility graph (mirrors synthesizePolygonInteriorEdges caps + fallback).
  const polygonForBPIP = turfPolygon(rings);
  const perimeterIds = new Set(perimeter.map((p) => coordToId(toLngLat(p))));
  const interiorSiblings: Position[] = [];
  const seen = new Set<string>();
  for (const ep of lineEndpoints) {
    const id = coordToId(toLngLat(ep));
    if (perimeterIds.has(id) || seen.has(id)) continue;
    if (booleanPointInPolygon(ep, polygonForBPIP, { ignoreBoundary: false })) {
      interiorSiblings.push(ep);
      seen.add(id);
    }
  }

  const totalCandidates = perimeter.length + interiorSiblings.length;
  const fallback =
    perimeter.length > PLAZA_PERIMETER_CAP || totalCandidates > PLAZA_TOTAL_CANDIDATE_CAP;

  if (fallback) {
    for (const sibling of interiorSiblings) {
      let nearestIdx = -1;
      let nearestDist = Infinity;
      const sLL = toLngLat(sibling);
      for (let i = 0; i < perimeter.length; i++) {
        const d = calcDistance(sLL, toLngLat(perimeter[i]));
        if (d < nearestDist) {
          nearestDist = d;
          nearestIdx = i;
        }
      }
      if (nearestIdx >= 0) pushEdge(sibling, perimeter[nearestIdx], "interior");
    }
    return;
  }

  const ringLineStrings = rings.map((r) => turfLineString(r));
  const candidates: Position[] = [...perimeter, ...interiorSiblings];
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      if (i < perimeter.length && j < perimeter.length) {
        const adj = j - i === 1 || (i === 0 && j === perimeter.length - 1);
        if (adj) continue; // already emitted as perimeter edge
      }
      const u = candidates[i];
      const v = candidates[j];
      const segment = turfLineString([u, v]);
      if (segmentCrossesAnyRing(segment, u, v, ringLineStrings)) continue;
      pushEdge(u, v, "interior");
    }
  }
}

/**
 * Permanent debug surface for routing investigations. Gated behind
 * `localStorage.wmRouteDebug = "1"` so it stays silent in normal operation but
 * gives us instant observability the next time admin and visitor disagree on
 * a route. Exposed as a single export both apps share.
 *
 * Tag distinguishes call sites (e.g. "[wm:net] admin" vs "[wm:net] visitor").
 * `inputs` carries the upstream feature counts so we can confirm the network
 * was built from the same raw material.
 */
export function isRouteDebugEnabled(): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem("wmRouteDebug") === "1";
  } catch {
    return false;
  }
}

export function logPathNetworkStats(
  network: PathNetwork,
  tag: string,
  inputs?: { polygonCount?: number; lineCount?: number }
): void {
  if (!isRouteDebugEnabled()) return;
  // BFS to count components.
  const seen = new Set<string>();
  let components = 0;
  for (const startId of network.nodes.keys()) {
    if (seen.has(startId)) continue;
    components++;
    const queue = [startId];
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const node = network.nodes.get(id);
      if (!node) continue;
      for (const n of node.connections) if (!seen.has(n)) queue.push(n);
    }
  }
  // eslint-disable-next-line no-console
  console.log(`[wm:net] ${tag}`, {
    nodes: network.nodes.size,
    edges: network.edges.size,
    components,
    ...(inputs ?? {}),
  });
}

/**
 * Classify a node for diagnostic output.
 * - "junction": canonical join-point. Either degree != 2 (endpoint or T/cross),
 *   or degree == 2 but the coord is in the supplied `lineEndpointKeys` /
 *   `polygonVertexKeys` set (a snap-on-draw merge of two endpoints into one
 *   "looks like" a degree-2 node but is semantically a junction).
 * - "polygon-vertex": plaza perimeter vertex (no sibling line endpoint).
 * - "midline": Chaikin-derived smoothing pass-through (degree exactly 2 and
 *   not in either reference set). Routes that snap to these on the visitor
 *   side but not on admin side cause the admin/visitor route divergence we
 *   observed in the Plan 115 Phase C wrap-up testing.
 */
export type NodeKind = "junction" | "polygon-vertex" | "midline";

export function classifyNode(
  node: PathNode,
  refs: { lineEndpointKeys: Set<string>; polygonVertexKeys: Set<string> }
): NodeKind {
  const isLineEndpoint = refs.lineEndpointKeys.has(node.id);
  const isPolygonVertex = refs.polygonVertexKeys.has(node.id);
  if (isLineEndpoint) return "junction";
  if (node.connections.length !== 2) return "junction";
  if (isPolygonVertex) return "polygon-vertex";
  return "midline";
}

/**
 * Build the reference key sets needed by `classifyNode` from the same input
 * features used to build the network. Cheap pass — same coord-iteration order
 * as `buildPathNetwork`.
 */
export function buildNodeKindRefs(
  pathFeatures: Feature<LineString | Polygon | MultiPolygon>[]
): { lineEndpointKeys: Set<string>; polygonVertexKeys: Set<string> } {
  const lineEndpointKeys = new Set<string>();
  const polygonVertexKeys = new Set<string>();
  for (const f of pathFeatures) {
    const geom = f.geometry;
    if (geom.type === "LineString") {
      const coords = geom.coordinates;
      if (coords.length >= 2) {
        lineEndpointKeys.add(coordToId(toLngLat(coords[0])));
        lineEndpointKeys.add(coordToId(toLngLat(coords[coords.length - 1])));
      }
    } else if (geom.type === "Polygon") {
      for (const ring of geom.coordinates as Position[][]) {
        for (const c of ring) polygonVertexKeys.add(coordToId(toLngLat(c)));
      }
    } else if (geom.type === "MultiPolygon") {
      for (const rings of geom.coordinates as Position[][][]) {
        for (const ring of rings) {
          for (const c of ring) polygonVertexKeys.add(coordToId(toLngLat(c)));
        }
      }
    }
  }
  return { lineEndpointKeys, polygonVertexKeys };
}

/**
 * Diagnostic — label every node in a routed path with its kind, and annotate
 * each consecutive pair with the edge distance from the graph (or "no-edge"
 * if the pair isn't directly connected — which is the signature of an off-path
 * jump in the rendered route line).
 *
 * Output shape: array of entries, one per coord in `path`. Each entry carries
 * `coord`, `kind` (junction/polygon-vertex/midline/off-graph), and for every
 * entry after the first, `edgeFromPrev`: { distance, hasEdge, weight }. A
 * `hasEdge: false` entry is the smoking gun — two consecutive coords in the
 * route that aren't connected by any edge in the graph means the route
 * renderer is drawing a straight line across whatever's between them.
 */
export interface RoutePathStep {
  coord: [number, number];
  /** Node classification. "virtual" covers edge-snap and plaza-interior
   *  virtual nodes from the overlay (Plan 121) — separate from the network's
   *  natural junction/polygon-vertex/midline taxonomy so diagnostic readers
   *  can distinguish "this is a snap node we injected" from "this is a real
   *  graph node." */
  kind: NodeKind | "virtual" | "off-graph";
  edgeFromPrev?: {
    distance: number | null;
    hasEdge: boolean;
    weight?: number;
    /** Edge classification (Plan 121). Lets diagnostic readers distinguish
     *  plaza-interior chords (abstract visibility edges) from line corridors
     *  and plaza perimeters at a glance. Undefined when hasEdge is false. */
    edgeKind?: PathEdgeKind;
  };
}

/**
 * @param overlay - Optional route overlay (Plan 121). When provided, virtual
 *   edges from edge-snap dispatch resolve correctly; without it, virtual
 *   edges show as `hasEdge: false`. Production callers should always pass
 *   `route.overlay` to avoid false "no-edge" reports.
 */
export function labelRoutePath(
  path: [number, number][],
  network: PathNetwork,
  refs: { lineEndpointKeys: Set<string>; polygonVertexKeys: Set<string> },
  overlay?: RouteOverlay
): RoutePathStep[] {
  const out: RoutePathStep[] = [];
  for (let i = 0; i < path.length; i++) {
    const coord = path[i];
    const id = coordToId(coord);
    // Overlay-aware node lookup. Virtual nodes (snap injections from Plan
    // 121) get their own "virtual" classification so they're not confused
    // with real junction/midline nodes via classifyNode's degree heuristic.
    const virtualNode = overlay?.virtualNodes.get(id);
    const networkNode = network.nodes.get(id);
    let kind: RoutePathStep["kind"];
    if (virtualNode) kind = "virtual";
    else if (networkNode) kind = classifyNode(networkNode, refs);
    else kind = "off-graph";
    const step: RoutePathStep = { coord, kind };
    if (i > 0) {
      const prevId = coordToId(path[i - 1]);
      const eid = prevId < id ? `${prevId}|${id}` : `${id}|${prevId}`;
      // Overlay wins on conflict (matches getEdge semantics).
      const edge = overlay
        ? overlay.virtualEdges.get(eid) ?? network.edges.get(eid)
        : network.edges.get(eid);
      step.edgeFromPrev = edge
        ? {
            distance: edge.distance,
            hasEdge: true,
            weight: edge.metadata?.wayfindingWeight,
            edgeKind: edge.kind,
          }
        : { distance: null, hasEdge: false };
    }
    out.push(step);
  }
  return out;
}

/**
 * Find the nearest graph node by Euclidean distance.
 *
 * Used for logical node association (POI bind, debug overlays, dev tooling).
 * For routing snap, use `findNearestEdgePoint` — it's geometrically correct
 * regardless of how sparsely lines are sampled. Node-snap is the cause of the
 * "user starts mid-line, route jumps across grass" class of bug (see Plan 121).
 *
 * @param network - The path network to search
 * @param point - Coordinates [lng, lat] to find nearest node to
 * @returns The nearest PathNode, or null if network is empty
 */
export function findNearestNode(
  network: PathNetwork,
  point: [number, number]
): PathNode | null {
  let nearestNode: PathNode | null = null;
  let nearestDist = Infinity;

  for (const node of network.nodes.values()) {
    const dist = calcDistance(point, node.coords);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearestNode = node;
    }
  }

  return nearestNode;
}

// ---------------------------------------------------------------------------
// Plan 121 commit 2 — Edge-snap + route-scoped virtual-node overlay
// ---------------------------------------------------------------------------

/**
 * Result of an edge-snap query. `snapCoord` is the geometrically closest
 * point on any edge to the input point. Callers should check whether
 * `snapCoord`'s coordToId matches an existing node (the canonical fast-path)
 * before deciding to inject a virtual node — see snap dispatch in commit 3.
 */
export interface EdgeSnapResult {
  edgeId: string;
  fromId: string;
  toId: string;
  snapCoord: [number, number];
  /** Meters from the input point to the snap point. */
  distFromInput: number;
  /** Meters along the edge from `fromId` to the snap point. Used by
   *  edge-split virtual-node distance math: `distAlong` to `fromId`,
   *  `edgeDist - distAlong` to `toId`. */
  distAlong: number;
  /** Total edge length in meters (mirrors `PathEdge.distance`). */
  edgeDist: number;
  edgeKind: PathEdgeKind;
  plazaId?: string;
}

/**
 * Find the geometrically closest point on any edge to the given input point.
 *
 * **Fast-path is the caller's responsibility, not this function's.** After
 * receiving a result, dispatch should do `network.nodes.get(coordToId(snapCoord))`
 * — if truthy, that's the existing node to use, no virtual injection. This
 * keeps the "are these the same point" decision routed through the canonical
 * `coordToId` helper (`spatial.ts`), avoiding a parallel distance-then-rounded
 * check that could disagree at the boundary.
 *
 * @param network - The path network to search
 * @param point - Coordinates [lng, lat] to snap from
 * @returns EdgeSnapResult for the closest edge, or null if network has no edges
 */
export function findNearestEdgePoint(
  network: PathNetwork,
  point: [number, number]
): EdgeSnapResult | null {
  let best: EdgeSnapResult | null = null;
  let bestDist = Infinity;
  const inputPt = turfPoint(point);

  for (const [eid, edge] of network.edges) {
    const fromNode = network.nodes.get(edge.from);
    const toNode = network.nodes.get(edge.to);
    if (!fromNode || !toNode) continue;

    const edgeLine = turfLineString([fromNode.coords, toNode.coords]);
    const snapped = nearestPointOnLine(edgeLine, inputPt, { units: "meters" });
    const dist = snapped.properties.dist;
    if (typeof dist !== "number" || !Number.isFinite(dist)) continue;

    if (dist < bestDist) {
      bestDist = dist;
      const snapCoord = snapped.geometry.coordinates as [number, number];
      const location = snapped.properties.location;
      best = {
        edgeId: eid,
        fromId: edge.from,
        toId: edge.to,
        snapCoord,
        distFromInput: dist,
        distAlong: typeof location === "number" ? location : 0,
        edgeDist: edge.distance,
        edgeKind: edge.kind,
        plazaId: edge.plazaId,
      };
    }
  }

  return best;
}

/**
 * Route-scoped overlay holding virtual nodes/edges injected at snap time.
 *
 * The original `PathNetwork` stays immutable across route calls; A* consults
 * both via `getConnections` and `getEdge`. This keeps cache ownership clean
 * (the cached network is read-only) and avoids the foot-gun of accidentally
 * mutating a memoized graph mid-render.
 */
export interface RouteOverlay {
  /** Virtual nodes (typically "vstart", "vend", and any same-edge intermediates).
   *  Their `connections` arrays point at both real network IDs and other
   *  virtual IDs in this overlay. */
  virtualNodes: Map<string, PathNode>;
  /** Virtual edges keyed by `edgeId(from, to)`. Includes both
   *  virtual↔real and virtual↔virtual edges. */
  virtualEdges: Map<string, PathEdge>;
  /** Original-network node ID → list of virtual node IDs it should also be
   *  considered connected to. Lets A* discover virtual neighbors when
   *  traversing **from** an original node. (The reverse direction, virtual
   *  node → real node, is already covered by the virtual node's own
   *  `connections` array.) */
  extraConnections: Map<string, string[]>;
}

export function createEmptyOverlay(): RouteOverlay {
  return {
    virtualNodes: new Map(),
    virtualEdges: new Map(),
    extraConnections: new Map(),
  };
}

/**
 * Get all neighbors of `nodeId`, drawn from both the original network and
 * the overlay. Used by A* in commit 3 to traverse without mutating the
 * cached network.
 *
 * Order: network connections first, then virtual-node's own connections (if
 * it's a virtual node), then extra connections (if it's a real node that
 * picked up overlay neighbors). Duplicates are not deduplicated — callers
 * that care should use a Set, but A* tolerates revisits via gScore.
 */
export function getConnections(
  network: PathNetwork,
  overlay: RouteOverlay,
  nodeId: string
): string[] {
  const out: string[] = [];
  const networkNode = network.nodes.get(nodeId);
  if (networkNode) out.push(...networkNode.connections);
  const virtualNode = overlay.virtualNodes.get(nodeId);
  if (virtualNode) out.push(...virtualNode.connections);
  const extra = overlay.extraConnections.get(nodeId);
  if (extra) out.push(...extra);
  return out;
}

/**
 * Look up an edge by node-pair, checking overlay first then the network.
 * Overlay wins on conflict — required for the same-edge case where the
 * original edge between `fromId` and `toId` is logically replaced by a chain
 * of virtual segments.
 */
export function getEdge(
  network: PathNetwork,
  overlay: RouteOverlay,
  fromId: string,
  toId: string
): PathEdge | undefined {
  const eid = edgeId(fromId, toId);
  return overlay.virtualEdges.get(eid) ?? network.edges.get(eid);
}

/**
 * Look up a node by ID, checking overlay first then the network. A* needs
 * this for both virtual and real nodes (heuristic distance + path
 * reconstruction).
 */
export function getNode(
  network: PathNetwork,
  overlay: RouteOverlay,
  nodeId: string
): PathNode | undefined {
  return overlay.virtualNodes.get(nodeId) ?? network.nodes.get(nodeId);
}
